import axios from "axios";

export const BASE_URL = import.meta.env.VITE_API_URL || `https://api.interviewzen.com`;

const axiosInstance = axios.create({
  baseURL: BASE_URL,
});

// Global token cache to ensure all requests use the same token
let cachedToken = null;
let tokenTimestamp = 0;
const TOKEN_CACHE_DURATION = 30000; // 30 seconds

axiosInstance.interceptors.request.use(
  async (config) => {
    try {
      const { Auth } = await import('aws-amplify');
      const now = Date.now();
      
      // Check if we have a fresh cached token
      if (cachedToken && (now - tokenTimestamp) < TOKEN_CACHE_DURATION) {
        config.headers.Authorization = `Bearer ${cachedToken}`;
        return config;
      }
      
      let accessToken = null;
      
      try {
        // Force fresh session
        const session = await Auth.currentSession();
        // Use ID token instead of access token - ID tokens have 'aud' claim required by API Gateway
        accessToken = session.getIdToken().getJwtToken();
      } catch (authError) {
        try {
          // Force complete refresh
          await Auth.currentAuthenticatedUser({ bypassCache: true });
          const session = await Auth.currentSession();
          // Use ID token instead of access token - ID tokens have 'aud' claim required by API Gateway
          accessToken = session.getIdToken().getJwtToken();
        } catch (refreshError) {
          console.error("Authentication failed:", refreshError);
          throw new Error("Unable to obtain valid token");
        }
      }
      
      if (accessToken) {
        // Cache the token globally for all subsequent requests
        cachedToken = accessToken;
        tokenTimestamp = now;
        
        config.headers.Authorization = `Bearer ${accessToken}`;
        localStorage.setItem("aws-token", accessToken);
      } else {
        throw new Error("No valid authentication token");
      }
      
    } catch (error) {
      console.error("Error in token retrieval:", error);
      // Don't fail the request, let the server handle auth errors
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

axiosInstance.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    // Log server errors for debugging
    if (error?.response?.status >= 500) {
      console.error("Server Error:", {
        url: error.config?.url,
        status: error.response?.status,
        message: error.response?.data?.message || error.response?.statusText
      });
    }
    
    return Promise.reject(error);
  }
);

export default axiosInstance;